import React, { useEffect, useState, useContext } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { Context } from '../index';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box } from '@mui/system';
import {
  TableContainer,
  IconButton,
  TextField,
  TableBody,
  TableCell,
  TableRow,
  Table,
  Button,
  Typography,
  Paper,
  Tooltip,
  Checkbox,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';

import {
  fetchChatCategories,
  deleteChatCategory,
  createOrUpdateChatCategory,
  reorderChatCategories,
  transferChatCategoryChats,
} from '../http/categoryAPI';

const ChatCategories = () => {
  const { user } = useContext(Context);
  const [categories, setCategories] = useState([]);
  const [name, setName] = useState('');
  const [isVisibleInBot, setIsVisibleInBot] = useState(true);
  const [initialMessage, setInitialMessage] = useState('');
  const [messageToUserAfterSentToManager, setMessageToUserAfterSentToManager] = useState('');
  const [expandedRows, setExpandedRows] = useState({});
  const [isExternalLink, setIsExternalLink] = useState(false);
  const [externalLinkUrl, setExternalLinkUrl] = useState('');
  const [externalLinkToggles, setExternalLinkToggles] = useState({});
  const [externalLinkDrafts, setExternalLinkDrafts] = useState({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteDialogCategory, setDeleteDialogCategory] = useState(null);
  const [deleteDialogTransferCategoryId, setDeleteDialogTransferCategoryId] = useState('');
  const [isDeletingCategory, setIsDeletingCategory] = useState(false);

  const isAddDisabled =
    !name.trim() || (isExternalLink && !externalLinkUrl.trim());
  
  const canManageCategories = user.user?.access?.manageChatCategories || user.user?.access?.isOwner;

  const toggleRow = (categoryId) => {
    setExpandedRows(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      // Только владельцы и те, у кого есть manageChatCategories могут видеть настройки
      if (user.user?.access?.isOwner || user.user?.access?.manageChatCategories) {
        const data = await fetchChatCategories();
        setCategories(data);
      } else {
        setCategories([]);
      }
    } catch (error) {
      console.error('Ошибка при загрузке категорий:', error);
    }
  };

  const addCategory = () => {
    if (!name.trim()) return;

    if (isExternalLink && !externalLinkUrl.trim()) {
      alert('Укажите ссылку на внешний чат');
      return;
    }

    createOrUpdateChatCategory({ 
      name, 
      isVisibleInBot,
      initialMessage: isExternalLink ? null : (initialMessage.trim() || null),
      messageToUserAfterSentToManager: isExternalLink ? null : (messageToUserAfterSentToManager.trim() || null),
      isExternalLink,
      externalLinkUrl: isExternalLink ? (externalLinkUrl.trim() || null) : null
    }).then((data) => {
      if (!data.error) {
        setCategories([...categories, data]);
        setName('');
        setIsVisibleInBot(true);
        setInitialMessage('');
        setMessageToUserAfterSentToManager('');
        setIsExternalLink(false);
        setExternalLinkUrl('');
      }
    });
  };

  const openDeleteDialog = (category) => {
    if (category.isDefault) {
      alert('Нельзя удалить категорию "по умолчанию"');
      return;
    }

    const hasChats = (category.chats?.length || 0) > 0;
    const availableCategories = categories.filter((cat) => cat.id !== category.id);

    if (!hasChats) {
      if (!window.confirm('Вы уверены, что хотите удалить эту категорию?')) {
        return;
      }
      removeCategory(category.id);
      return;
    }

    if (availableCategories.length === 0) {
      alert('Нельзя удалить категорию с чатами, пока нет другой категории для переноса');
      return;
    }

    setDeleteDialogCategory(category);
    setDeleteDialogTransferCategoryId(availableCategories[0].id || '');
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    if (isDeletingCategory) return;
    setDeleteDialogOpen(false);
    setDeleteDialogCategory(null);
    setDeleteDialogTransferCategoryId('');
  };

  const removeCategory = (id) => {
    setIsDeletingCategory(true);
    deleteChatCategory(id).then((data) => {
      if (!data.error) {
        setCategories((current) =>
          current.filter((category) => category.id !== id)
        );
      }
    }).catch((err) => {
      alert(err?.response?.data?.error || 'Ошибка при удалении категории');
    }).finally(() => {
      setIsDeletingCategory(false);
    });
  };

  const confirmDeleteWithTransfer = () => {
    if (!deleteDialogCategory) return;

    if (!deleteDialogTransferCategoryId) {
      alert('Выберите категорию для переноса');
      return;
    }

    setIsDeletingCategory(true);

    transferChatCategoryChats(deleteDialogCategory.id, deleteDialogTransferCategoryId)
      .then(() => deleteChatCategory(deleteDialogCategory.id))
      .then((data) => {
        if (!data.error) {
          setCategories((current) =>
            current.filter((category) => category.id !== deleteDialogCategory.id)
          );
          closeDeleteDialog();
        }
      })
      .catch((err) => {
        alert(err?.response?.data?.error || 'Ошибка при переносе чатов');
      })
      .finally(() => {
        setIsDeletingCategory(false);
      });
  };

  const renameCategory = (category, newName) => {
    if (!newName.trim()) return;
    
    createOrUpdateChatCategory({ 
      id: category.id, 
      name: newName,
      isVisibleInBot: category.isVisibleInBot,
      initialMessage: category.initialMessage,
      messageToUserAfterSentToManager: category.messageToUserAfterSentToManager
    }).then((data) => {
      if (!data.error) {
        setCategories((current) =>
          current.map((cat) => {
            if (cat.id === category.id) {
              // Сохраняем старые значения chats
              return { ...data, chats: cat.chats };
            }
            return cat;
          })
        );
      }
    });
  };

  const updateInitialMessage = (category, newMessage) => {
    createOrUpdateChatCategory({ 
      id: category.id, 
      name: category.name,
      isVisibleInBot: category.isVisibleInBot,
      initialMessage: newMessage.trim() || null,
      messageToUserAfterSentToManager: category.messageToUserAfterSentToManager
    }).then((data) => {
      if (!data.error) {
        setCategories((current) =>
          current.map((cat) => {
            if (cat.id === category.id) {
              // Сохраняем старые значения chats
              return { ...data, chats: cat.chats };
            }
            return cat;
          })
        );
      }
    });
  };

  const updateMessageToUserAfterSentToManager = (category, newMessage) => {
    createOrUpdateChatCategory({ 
      id: category.id, 
      name: category.name,
      isVisibleInBot: category.isVisibleInBot,
      initialMessage: category.initialMessage,
      messageToUserAfterSentToManager: newMessage.trim() || null
    }).then((data) => {
      if (!data.error) {
        setCategories((current) =>
          current.map((cat) => {
            if (cat.id === category.id) {
              // Сохраняем старые значения chats
              return { ...data, chats: cat.chats };
            }
            return cat;
          })
        );
      }
    });
  };

  const updateExternalLinkSettings = (category, nextIsExternalLink, nextExternalLinkUrl) => {
    createOrUpdateChatCategory({
      id: category.id,
      name: category.name,
      isVisibleInBot: category.isVisibleInBot,
      initialMessage: category.initialMessage,
      messageToUserAfterSentToManager: category.messageToUserAfterSentToManager,
      isExternalLink: nextIsExternalLink,
      externalLinkUrl: nextExternalLinkUrl?.trim() || null
    }).then((data) => {
      if (!data.error) {
        setCategories((current) =>
          current.map((cat) => {
            if (cat.id === category.id) {
              return { ...data, chats: cat.chats };
            }
            return cat;
          })
        );
        setExternalLinkToggles((current) => {
          const next = { ...current };
          delete next[category.id];
          return next;
        });
        setExternalLinkDrafts((current) => {
          const next = { ...current };
          delete next[category.id];
          return next;
        });
      }
    });
  };

  const getCategoryIsExternalLink = (category) => {
    if (Object.prototype.hasOwnProperty.call(externalLinkToggles, category.id)) {
      return externalLinkToggles[category.id];
    }
    return !!category.isExternalLink;
  };

  const getCategoryExternalLinkUrl = (category) => {
    if (Object.prototype.hasOwnProperty.call(externalLinkDrafts, category.id)) {
      return externalLinkDrafts[category.id];
    }
    return category.externalLinkUrl || '';
  };

  const handleExternalLinkToggle = (category, checked) => {
    setExternalLinkToggles((current) => ({
      ...current,
      [category.id]: checked
    }));

    if (!checked) {
      updateExternalLinkSettings(category, false, category.externalLinkUrl || null);
      return;
    }

    const draft = getCategoryExternalLinkUrl(category);
    if (draft.trim()) {
      updateExternalLinkSettings(category, true, draft);
    }
  };

  const handleExternalLinkDraftChange = (category, value) => {
    setExternalLinkDrafts((current) => ({
      ...current,
      [category.id]: value
    }));
  };

  const handleExternalLinkDraftBlur = (category) => {
    if (!getCategoryIsExternalLink(category)) return;

    const draft = getCategoryExternalLinkUrl(category);
    if (!draft.trim()) {
      alert('Укажите ссылку на внешний чат');
      return;
    }

    if (draft.trim() === (category.externalLinkUrl || '')) {
      return;
    }

    updateExternalLinkSettings(category, true, draft);
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(categories);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setCategories(items);

    const categoriesWithIndex = items.map((cat, index) => ({
      id: cat.id,
      index: index,
    }));

    reorderChatCategories(categoriesWithIndex).catch(() => {
      loadCategories();
    });
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Категории чатов
      </Typography>

      {canManageCategories && (
        <Paper sx={{ p: 2, mb: 3, backgroundColor: '#f5f5f5' }}>
          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
            Создать новую категорию
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <TextField
                value={name}
                onChange={(e) => setName(e.target.value)}
                label="Название категории"
                variant="outlined"
                size="small"
                sx={{ flex: '1 1 240px', minWidth: 220 }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    addCategory();
                  }
                }}
              />
              <Box sx={{ width: 1 }} />
              <Box sx={{ minWidth: 120 }}>
              <Button
                variant="contained"
                startIcon={<AddCircleOutlineIcon />}
                onClick={addCategory}
                disabled={isAddDisabled}
                sx={{ whiteSpace: 'nowrap' }}
              >
                Добавить
              </Button>
              </Box>
            </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Checkbox
              checked={isExternalLink}
              onChange={(e) => setIsExternalLink(e.target.checked)}
              size="small"
            />
            <Typography variant="caption">Внешняя ссылка на чат</Typography>
          </Box>
          {isExternalLink && (
            <TextField
              value={externalLinkUrl}
              onChange={(e) => setExternalLinkUrl(e.target.value)}
              label="Ссылка на чат"
              variant="outlined"
              size="small"
              fullWidth
              placeholder="https://t.me/..."
            />
          )}
          {!isExternalLink && (
            <>
              <TextField
                value={initialMessage}
                onChange={(e) => setInitialMessage(e.target.value)}
                label="Начальное сообщение (необязательно)"
                variant="outlined"
                size="small"
                fullWidth
                multiline
                rows={2}
                placeholder="Если не указано, будет использовано стандартное сообщение"
                helperText="Это сообщение увидит пользователь при открытии чата с менеджером в данной категории"
              />
              <TextField
                value={messageToUserAfterSentToManager}
                onChange={(e) => setMessageToUserAfterSentToManager(e.target.value)}
                label="Сообщение после отправки менеджеру (необязательно)"
                variant="outlined"
                size="small"
                fullWidth
                multiline
                rows={2}
                placeholder="Если не указано, будет использовано стандартное сообщение"
                helperText="Это сообщение увидит пользователь после отправки первого сообщения менеджеру"
              />
            </>
          )}
        </Box>
      </Paper>
      )}

      <TableContainer component={Paper}>
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="categories">
            {(provided) => (
              <Table ref={provided.innerRef} {...provided.droppableProps}>
                <TableBody>
                  {categories.map((category, index) => (
                    <React.Fragment key={category.id}>
                      <Draggable
                        draggableId={category.id}
                        index={index}
                      >
                        {(provided) => (
                          <TableRow
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                          >
                            {canManageCategories && (
                              <TableCell sx={{ width: 50 }}>
                                <div {...provided.dragHandleProps}>
                                  <DragIndicatorIcon />
                                </div>
                              </TableCell>
                            )}
                            <TableCell>
                              {canManageCategories ? (
                                <TextField
                                  defaultValue={category.name}
                                  onBlur={(e) => {
                                    if (e.target.value !== category.name) {
                                      renameCategory(category, e.target.value);
                                    }
                                  }}
                                  onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                      e.target.blur();
                                    }
                                  }}
                                  size="small"
                                  fullWidth
                                />
                              ) : (
                                <Typography>{category.name}</Typography>
                              )}
                            </TableCell>
                            <TableCell sx={{ width: 100 }}>
                              {category.isDefault && (
                                <Typography variant="caption" color="primary">
                                  По умолчанию
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell sx={{ width: 100 }} />
                            <TableCell sx={{ width: 100 }}>
                              <Typography
                                variant="caption"
                                color="textSecondary"
                                sx={{
                                  visibility: getCategoryIsExternalLink(category) ? 'hidden' : 'visible'
                                }}
                              >
                                Чатов: {category.chats?.length || 0}
                              </Typography>
                            </TableCell>
                            {canManageCategories && (
                              <>
                                <TableCell sx={{ width: 80 }}>
                                  <Tooltip
                                    title={
                                      getCategoryIsExternalLink(category)
                                        ? 'Настроить внешнюю ссылку'
                                        : 'Настроить начальное сообщение'
                                    }
                                  >
                                    <IconButton
                                      onClick={() => toggleRow(category.id)}
                                      size="small"
                                      sx={{
                                        transform: expandedRows[category.id] ? 'rotate(180deg)' : 'rotate(0deg)',
                                        transition: 'transform 0.3s',
                                      }}
                                    >
                                      <ExpandMoreIcon />
                                    </IconButton>
                                  </Tooltip>
                                </TableCell>
                                <TableCell sx={{ width: 50 }}>
                                  <IconButton
                                    onClick={() => openDeleteDialog(category)}
                                    size="small"
                                    color="error"
                                    disabled={category.isDefault}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </TableCell>
                              </>
                            )}
                          </TableRow>
                        )}
                      </Draggable>
                      {canManageCategories && (
                        <TableRow>
                          <TableCell colSpan={7} sx={{ py: 0, border: 0 }}>
                            <Collapse in={expandedRows[category.id]} timeout="auto" unmountOnExit>
                              <Box sx={{ p: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                                  <Checkbox
                                    checked={getCategoryIsExternalLink(category)}
                                    onChange={(e) => handleExternalLinkToggle(category, e.target.checked)}
                                    size="small"
                                  />
                                  <Typography variant="caption">Внешняя ссылка на чат</Typography>
                                </Box>
                                {getCategoryIsExternalLink(category) ? (
                                  <TextField
                                    value={getCategoryExternalLinkUrl(category)}
                                    onChange={(e) => handleExternalLinkDraftChange(category, e.target.value)}
                                    onBlur={() => handleExternalLinkDraftBlur(category)}
                                    label="Ссылка на чат"
                                    placeholder="https://t.me/..."
                                    size="small"
                                    fullWidth
                                  />
                                ) : (
                                  <>
                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                      Начальное сообщение при открытии чата
                                    </Typography>
                                    <TextField
                                      defaultValue={category.initialMessage || ''}
                                      onBlur={(e) => {
                                        if (e.target.value !== (category.initialMessage || '')) {
                                          updateInitialMessage(category, e.target.value);
                                        }
                                      }}
                                      placeholder="Если не указано, будет использовано стандартное сообщение"
                                      helperText="Это сообщение увидит пользователь при открытии чата с менеджером в данной категории"
                                      size="small"
                                      fullWidth
                                      multiline
                                      rows={3}
                                      sx={{ mb: 2 }}
                                    />
                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                      Сообщение после отправки первого сообщения менеджеру
                                    </Typography>
                                    <TextField
                                      defaultValue={category.messageToUserAfterSentToManager || ''}
                                      onBlur={(e) => {
                                        if (e.target.value !== (category.messageToUserAfterSentToManager || '')) {
                                          updateMessageToUserAfterSentToManager(category, e.target.value);
                                        }
                                      }}
                                      placeholder="Если не указано, будет использовано стандартное сообщение"
                                      helperText="Это сообщение увидит пользователь после отправки первого сообщения менеджеру"
                                      size="small"
                                      fullWidth
                                      multiline
                                      rows={3}
                                    />
                                  </>
                                )}
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
                  {provided.placeholder}
                </TableBody>
              </Table>
            )}
          </Droppable>
        </DragDropContext>
      </TableContainer>

      <Dialog
        open={deleteDialogOpen}
        onClose={closeDeleteDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Удаление категории</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Категория "{deleteDialogCategory?.name}" содержит чаты. Укажите, в какую категорию перенести их перед удалением.
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel id="transfer-category-label">Категория для переноса</InputLabel>
            <Select
              labelId="transfer-category-label"
              label="Категория для переноса"
              value={deleteDialogTransferCategoryId}
              onChange={(e) => setDeleteDialogTransferCategoryId(e.target.value)}
              disabled={isDeletingCategory}
            >
              {categories
                .filter((cat) => cat.id !== deleteDialogCategory?.id)
                .map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>
                    {cat.name}
                    {cat.isDefault ? ' (по умолчанию)' : ''}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} disabled={isDeletingCategory}>
            Отмена
          </Button>
          <Button
            onClick={confirmDeleteWithTransfer}
            variant="contained"
            color="error"
            disabled={!deleteDialogTransferCategoryId || isDeletingCategory}
          >
            Перенести и удалить
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ChatCategories;
